import { inject, injectable } from 'inversify';
import type { Composer } from 'vue-i18n';
import type { SideNavItem, SideNavSection } from '@design-system/side-nav/application/component/SideNav/SideNav.types';
import { CockpitNavigationService } from '@client/composition/main-layout/domain/service/navigation.service';
import { COCKPIT_NAVIGATION_SECTION_IDS } from '@client/composition/main-layout/domain/constant/cockpit-navigation-section-ids.constant';
import { MainLayoutDependencyTypes } from '@client/composition/main-layout/main-layout.dependency-types';
import { GameKind } from '@client/entity/game/common/core/domain/constant/game-kind.constant';
import { GameDetailRouteName } from '@client/feature/game-detail/domain/route/game-detail.route-name';
import { IconName, type IconNameValue } from '@kiplin/web-common/icon';
import type { CurrentClientUserClient } from '@client/entity/current-client-user/domain/model/current-client-user-client.model';
import { TranslationDependencyTypes } from '@kiplin/web-common/translation';
import type { TrackingService } from '@shared/common/tracking/domain/service/tracking.service';
import { TrackingDependencyTypes } from '@shared/common/tracking/tracking.dependency-types';
import { HomeRouteName } from '@shared/feature/home/domain/route/home.route-name';

@injectable()
export class MainLayoutService {
  public constructor(
    @inject(MainLayoutDependencyTypes.CockpitNavigationService)
    private readonly navigationService: CockpitNavigationService,
    @inject(TranslationDependencyTypes.i18n)
    private readonly i18n: Composer,
    @inject(TrackingDependencyTypes.TrackingService)
    private readonly trackingService: TrackingService,
  ) {}

  public buildGamesNavigationSections(userClient: CurrentClientUserClient | null): SideNavSection[] {
    const navigationSections: SideNavSection[] = this.navigationService.getDefaultSections();
    if (null === userClient) {
      return navigationSections;
    }

    const homeSection = this.getHomeSection();
    if (homeSection) {
      navigationSections.push(homeSection);
    }

    if (userClient.hasActiveSubscription) {
      navigationSections.push(this.navigationService.getSubscriptionSection(userClient));
    }

    const gamesNavItems: SideNavItem[] = userClient.games.map((game) => {
      const isKnownType = this.isKnownGameKind(game.type);

      return {
        id: game.id,
        label: game.title,
        icon: this.getGameIcon(game.type),
        disabled: !isKnownType,
        captureButtonName: 'navigate-' + game.type,
        associatedRouteName: GameDetailRouteName.Detail,
        associatedRouteParams: isKnownType ? { gameId: game.id, gameKind: game.type } : undefined,
      };
    });

    navigationSections.push(this.navigationService.getGameSection(gamesNavItems));

    return navigationSections;
  }

  private getHomeSection(): SideNavSection | null {
    return {
      id: COCKPIT_NAVIGATION_SECTION_IDS.HOME,
      dividerAfter: true,
      items: [
        {
          id: HomeRouteName.Home,
          label: this.i18n.t('common.side-nav.dashboard'),
          icon: IconName.Home,
          associatedRouteName: HomeRouteName.Home,
        },
      ],
    };
  }

  private getGameIcon(kind: GameKind): IconNameValue {
    switch (kind) {
      case GameKind.Challenge:
        return IconName.Trophy;
      case GameKind.Racing:
        return IconName.RacingFlag;
      case GameKind.Kwiz:
        return IconName.Pawn;
      case GameKind.Adventure:
        return IconName.Map;
      default:
        return IconName.Delete;
    }
  }

  private isKnownGameKind(value: unknown): value is GameKind {
    return (
      value === GameKind.Challenge ||
      value === GameKind.Racing ||
      value === GameKind.Kwiz ||
      value === GameKind.Adventure
    );
  }
}
